import { useApolloClient } from '@apollo/client';
import {
  Button,
  CheckboxFormField,
  DatePickerFormField,
  Form,
  IFormRender,
  Justify,
  Label,
  Loader,
  Row,
  TextFormField,
  Theme,
  Toast,
} from '@busybusy/webapp-react-ui';
import FlexContainer from '@busybusy/webapp-react-ui/dist/components/FlexContainer/FlexContainer';
import {
  Equipment,
  EquipmentCostHistory,
  EquipmentModelCreateInput,
  OrganizationCustomFieldsTypes,
} from '__generated__/graphql';
import { EQUIPMENT_BY_ID_QUERY } from 'apollo/queries/equipment-queries';
import classNames from 'classnames';
import { ProfileImageFormField } from 'components';
import EquipmentCategoryFormField from 'components/domain/equipment/EquipmentCategoryFormField/EquipmentCategoryFormField';
import EquipmentMakeFormField from 'components/domain/equipment/EquipmentMakeFormField/EquipmentMakeFormField';
import EquipmentModelFormField from 'components/domain/equipment/EquipmentModelFormField/EquipmentModelFormField';
import { equipmentUnknownModelQuery } from 'components/domain/equipment/EquipmentModelsForMakePicker/equipment-models-picker-queries';
import EquipmentTypeFormField from 'components/domain/equipment/EquipmentTypeFormField/EquipmentTypeFormField';
import FeatureTimeFormField from 'components/domain/time/FeatureTimeFormField/FeatureTimeFormField';
import CurrencyFormField from 'components/foundation/form-fields/CurrencyFormField/CurrencyFormField';
import Spinner from 'components/foundation/Spinner/Spinner';
import useEquipmentChecklistRoutines from 'containers/equipment-inspections/hooks/useEquipmentChecklistRoutines';
import useEquipmentInspections from 'containers/equipment-inspections/hooks/useEquipmentInspections';
import useEquipmentImageUpdate from 'containers/manage-employees/hooks/useEquipmentImageUpdate';
import useEquipmentModels from 'containers/manage-euipment/hooks/useEquipmentModels';
import { useActiveMember, useEquipment, useEquipmentHours, useOpenable } from 'hooks';
import useCreateEquipmentCostHistory from 'hooks/models/equipment-cost-history/useCreateEquipmentCostHistory';
import useUpdateEquipmentCostHistory from 'hooks/models/equipment-cost-history/useUpdateEquipmentCostHistory';
import useDeviceRpcService from 'hooks/models/equipment/useDeviceRpcService';
import useEquipmentDevices, { IEquipmentDevice } from 'hooks/models/equipment/useEquipmentDevices';
import useOrganizationCustomFields from 'hooks/models/organization-custom-fields/useOrganizationCustomFields';
import useOnMount from 'hooks/utils/useOnMount/useOnMount';
import _, { isNil } from 'lodash';
import { DateTime } from 'luxon';
import { ReactNode, useRef, useState } from 'react';
import { ClassName } from 'types/ClassName';
import ClockAction from 'types/enum/ClockAction';
import EquipmentModel from 'types/EquipmentModel';
import { isoTimeStampUtc } from 'utils/dateUtils';
import { getCurrentEquipmentCostHistory, getDeviceCoupleErrorMessage, getLimitingMaxDate } from 'utils/equipmentUtils';
import { t } from 'utils/localize';
import { convertStringToSafeNumber } from 'utils/numberUtils';
import { extractCustomFields, ICustomFieldTableRow } from 'utils/organizationCustomFieldsUtils';
import { isAdmin } from 'utils/positionUtils';
import { v_number_decimals_only, v_require, v_time_before_time } from 'utils/validations';
import { IEquipmentCreateFormData } from '../EquipmentCreateForm/EquipmentCreateForm';
import EquipmentGpsDeviceAlreadyAssignedDialog from '../EquipmentGpsDeviceAlreadyAssignedDialog/EquipmentGpsDeviceAlreadyAssignedDialog';
import EquipmentGpsTrackerFormField from '../EquipmentGpsTrackerFormField/EquipmentGpsTrackerFormField';
import EquipmentInspectionChecklistPickerFormField from '../EquipmentInspectionChecklistPickerFormField/EquipmentInspectionChecklistPickerFormField';
import { IExistingModel } from '../EquipmentModelPicker/EquipmentModelPicker';
import EquipmentTypesDialog from '../EquipmentTypesDialog/EquipmentTypesDialog';
import useIntegratedEquipmentEnabled from '../IntegratedEquipmentLocationDialog/hooks/useIntegratedEquipmentEnabled';
import ManageEquipmentCategoriesDialog from '../ManageEquipmentCategoriesDialog/ManageEquipmentCategoriesDialog';
import ManageEquipmentMakesDialog from '../ManageEquipmentMakesDialog/ManageEquipmentMakesDialog';
import RoutineInspectionFields from '../RoutineInspectionFields/RoutineInspectionFields';
import {
  EQUIPMENT_MAX_ROUTINE_INSPECTIONS,
  exceedsRoutineInspectionLimit,
  getRoutineFormData,
  getRoutineItemsFromFormData,
  parseRoutineInterval,
  reconcileRoutineInspections,
} from '../RoutineInspectionFields/routineInspectionFormUtils';
import UnpairTrackingDeviceDialog from '../UnpairTrackingDeviceDialog/UnpairTrackingDeviceDialog';
import './EditEquipmentForm.scss';

export interface IEditEquipmentFormProps {
  className?: ClassName;
  equipmentId: string;
  editKey: string | null;
  onSubmit: (data?: string) => void;
}

const EditEquipmentForm = (props: IEditEquipmentFormProps) => {
  const { className, equipmentId, editKey, onSubmit } = props;

  const { editEquipment } = useEquipment();
  const { createEquipmentHours } = useEquipmentHours();
  const { createEquipmentModel } = useEquipmentModels();
  const { getOrganizationCustomFields } = useOrganizationCustomFields();
  const createEquipmentCostHistory = useCreateEquipmentCostHistory();
  const updateEquipmentCostHistory = useUpdateEquipmentCostHistory();
  const apolloClient = useApolloClient();
  const imageUpdate = useEquipmentImageUpdate();
  const hasGpsHardware = useIntegratedEquipmentEnabled();
  const fetchDevices = useEquipmentDevices();
  const authMember = useActiveMember();
  const authMemberIsAdmin = isAdmin(authMember.position);
  const deviceRpcService = useDeviceRpcService();

  const [formData, setFormData] = useState<IEquipmentCreateFormData>();
  const [reloadCategories, setReloadCategories] = useState(false);
  const [modelToCreate, setModelToCreate] = useState<{ id: string | null; name: string | null }>();
  const [customFields, setCustomFields] = useState<ICustomFieldTableRow[]>([]);
  const [devices, setDevices] = useState<IEquipmentDevice[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<IEquipmentDevice | null>(null);
  const [isLoadingDevices, setIsLoadingDevices] = useState(hasGpsHardware);

  const { createEquipmentChecklistRoutine } = useEquipmentChecklistRoutines();
  const { deleteUpcomingEquipmentInspections } = useEquipmentInspections();

  const errorToastMessage = useRef(t('There was an unexpected error.'));
  const latestCostHistory = useRef<EquipmentCostHistory | undefined>(undefined);
  const pendingSubmissionRef = useRef<IEquipmentCreateFormData | null>(null);

  const errorToastState = useOpenable();
  const loaderDetails = useOpenable();
  const dateDetails = useOpenable();
  const manageCategoriesOpenable = useOpenable();
  const manageMakesOpenable = useOpenable();
  const typesDialogOpenable = useOpenable();
  const deviceAlreadyAssignedOpenable = useOpenable();
  const unpairDeviceOpenable = useOpenable();
  const installationDateOpenable = useOpenable();

  const today = DateTime.local();

  const classes = classNames('edit-equipment-form', className);

  const getCustomFields = async () => {
    try {
      const organizationCustomFields = await getOrganizationCustomFields(OrganizationCustomFieldsTypes.Equipment);
      if (organizationCustomFields) {
        const extractedFields = extractCustomFields(organizationCustomFields);
        setCustomFields(extractedFields);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch custom fields:', error);
    }
  };

  const getDevices = async () => {
    setIsLoadingDevices(true);
    try {
      const allDevices = await fetchDevices(hasGpsHardware);
      setDevices(allDevices);
      const currentDevice = allDevices.find((device) => device.equipment_id === equipmentId) ?? null;
      setSelectedDevice(currentDevice);
      setFormData((prev) => ({
        ...prev!,
        gpsDeviceId: currentDevice?.id ? `${currentDevice.id}` : null,
        installationDate:
          currentDevice && currentDevice.current_coupled_date
            ? {
                value: DateTime.fromISO(currentDevice.current_coupled_date).toJSDate(),
                inputValue: DateTime.fromISO(currentDevice.current_coupled_date).toFormat('M/D/YYYY'),
              }
            : undefined,
        installationTime:
          currentDevice && currentDevice.current_coupled_date
            ? DateTime.fromISO(currentDevice.current_coupled_date)
            : undefined,
      }));
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const setDevice = (deviceId: string | null, form?: IFormRender<IEquipmentCreateFormData>) => {
    if (isNil(deviceId)) {
      form?.clearMultipleFieldErrors(['installationDate', 'installationTime']);
      setSelectedDevice(null);
      setFormData((prev) => ({
        ...prev!,
        gpsDeviceId: null,
        installationDate: undefined,
        installationTime: undefined,
      }));
      return;
    }

    const device = devices.find((d) => d.id === parseInt(deviceId, 10));
    setSelectedDevice(device ?? null);
    setFormData((prev) => ({
      ...prev!,
      gpsDeviceId: deviceId,
      installationDate: {
        value: today.toJSDate(),
        inputValue: today.toFormat('M/D/YYYY'),
      },
      installationTime: today,
    }));
  };

  useOnMount(() => {
    getEquipment().then((equipment) => {
      latestCostHistory.current = getCurrentEquipmentCostHistory(equipment!.costHistory ?? []);

      const hourlyCostEffectiveDate = !isNil(latestCostHistory.current?.changeDate)
        ? DateTime.fromISO(latestCostHistory.current!.changeDate)
        : today.startOf('day');

      getCustomFields();

      if (hasGpsHardware) {
        getDevices();
      }

      setFormData((prev) => ({
        ...prev,
        equipmentName: equipment!.equipmentName,
        type: equipment!.model?.type,
        categoryId: equipment!.model?.equipmentCategoryId,
        makeId: equipmentMakeId(equipment!),
        modelId: equipmentModelId(equipment!),
        modelTitle: equipmentModelTitle(equipment!),
        trackManualHours: equipment?.trackManualHours,
        hourMeter: equipment!.lastHours?.runningHours ?? 0,
        imageUrl: equipment?.imageUrl ?? undefined,
        hourlyCost: latestCostHistory.current?.operatorCostRate ?? 0,
        hourlyCostEffectiveDate: {
          value: hourlyCostEffectiveDate.toJSDate(),
          inputValue: hourlyCostEffectiveDate.toFormat('M/D/YYYY'),
        },
        year: equipment?.year ?? undefined,
        customField1: equipment?.customField1 ?? undefined,
        customField2: equipment?.customField2 ?? undefined,
        customField3: equipment?.customField3 ?? undefined,
        customField4: equipment?.customField4 ?? undefined,
        customField5: equipment?.customField5 ?? undefined,
        preOperationChecklistId: equipment?.preOperationChecklistId ?? undefined,
        postOperationChecklistId: equipment?.postOperationChecklistId ?? undefined,
        ...getRoutineFormData(equipment?.routines),
      }));
    });
  });

  const handleTimeChange = (value: DateTime | null | undefined, form: IFormRender<IEquipmentCreateFormData>) => {
    form.setData('installationTime', value);
  };

  const handleCategoriesDialogClose = () => {
    manageCategoriesOpenable.close();
    setReloadCategories((prev) => !prev);
  };

  const handleMakeOrCategoryChange = (form: IFormRender<IEquipmentCreateFormData>) => {
    form.setFormData({ ...form.state.data, modelId: undefined });
  };

  const handleExistingModelSelected = (model: IExistingModel, form: IFormRender<IEquipmentCreateFormData>) => {
    form.setFormData({
      ...form.state.data,
      makeId: model.makeId,
      categoryId: model.categoryId,
    });
  };

  const equipmentMakeId = (equipment: Equipment) => {
    if (equipment?.model?.make?.unknown === false) {
      return equipment.model.equipmentMakeId;
    }

    return undefined;
  };

  const equipmentModelId = (equipment: Equipment) => {
    if (equipment?.model?.unknown === false) {
      return equipment.model.id;
    }
    return undefined;
  };

  const equipmentModelTitle = (equipment: Equipment) => {
    if (equipment?.model?.unknown === false) {
      return equipment.model.title as string;
    }

    return undefined;
  };

  const getEquipment = async () => {
    const results = await apolloClient.query<{ equipment: Equipment[] }>({
      query: EQUIPMENT_BY_ID_QUERY,
      fetchPolicy: 'network-only',
      variables: {
        equipmentId,
      },
    });

    return _.first(results.data.equipment);
  };

  const equipmentNameField = (form: IFormRender<IEquipmentCreateFormData>) => (
    <>
      <Label required={true}>{t('Name')}</Label>
      <TextFormField name={'equipmentName'} form={form} validations={[{ validation: v_require }]} autofocus={true} />
    </>
  );

  const equipmentTypePicker = (form: IFormRender<IEquipmentCreateFormData>, autofocus: boolean = false) => (
    <>
      <Label>{t('Type')}</Label>
      <span className="types-help-text">
        {t('To learn more about types, ')}
        <span className="types-link" onClick={typesDialogOpenable.open}>
          {t('click here.')}
        </span>
      </span>
      <EquipmentTypeFormField
        name={'type'}
        form={form}
        validations={[{ validation: v_require }]}
        autofocus={autofocus}
        onChange={() => handleMakeOrCategoryChange(form)}
      />
    </>
  );

  const equipmentCategoryPicker = (form: IFormRender<IEquipmentCreateFormData>) => (
    <>
      <FlexContainer justifyContent={'space-between'}>
        <Label className={'py-2'}>{t('Category')}</Label>
        <Button type="link" onClick={manageCategoriesOpenable.open}>
          {t('Manage')}
        </Button>
      </FlexContainer>
      <EquipmentCategoryFormField
        name={'categoryId'}
        form={form}
        validations={[{ validation: v_require }]}
        reload={reloadCategories}
        onChange={() => handleMakeOrCategoryChange(form)}
        disabled={isNil(form.state.data.type)}
      />
    </>
  );

  const equipmentMakePicker = (form: IFormRender<IEquipmentCreateFormData>, autofocus: boolean = false) => (
    <>
      <FlexContainer justifyContent={'space-between'}>
        <Label className={'py-2'}>{t('Make')}</Label>
        <Button type="link" onClick={manageMakesOpenable.open}>
          {t('Manage')}
        </Button>
      </FlexContainer>
      <EquipmentMakeFormField
        name={'makeId'}
        equipmentCategoryId={form.state.data.categoryId}
        form={form}
        autofocus={autofocus}
        onChange={() => handleMakeOrCategoryChange(form)}
        disabled={isNil(form.state.data.type)}
        validations={[{ validation: v_require }]}
      />
    </>
  );

  const equipmentModelPicker = (form: IFormRender<IEquipmentCreateFormData>) => (
    <>
      <Label>{t('Model')}</Label>
      <EquipmentModelFormField
        name={'modelId'}
        equipmentMakeId={form.state.data.makeId}
        equipmentCategoryId={form.state.data.categoryId}
        equipmentModelType={form.state.data.type}
        form={form}
        onExistingModel={(model) => {
          handleExistingModelSelected(model, form);
        }}
        onNewModel={setModelToCreate}
        disabled={isNil(form.state.data.type)}
        validations={[{ validation: v_require }]}
      />
    </>
  );

  const equipmentYearFormField = (form: IFormRender<IEquipmentCreateFormData>, autofocus: boolean = false) => (
    <>
      <Label>{t('Year')}</Label>
      <TextFormField className="year-field" name="year" form={form} autofocus={autofocus} />
    </>
  );

  const equipmentHoursField = (form: IFormRender<IEquipmentCreateFormData>, autofocus: boolean = false) => (
    <>
      <Label>{t('Hour Meter')}</Label>
      <TextFormField
        className="hour-meter-field"
        type="number"
        name={'hourMeter'}
        placeholder={'0.0'}
        form={form}
        restrictTo={{ maxLength: 10 }}
        validations={[{ validation: v_number_decimals_only }]}
        autofocus={autofocus}
      />
    </>
  );

  const trackHoursCheckbox = (form: IFormRender<IEquipmentCreateFormData>) => (
    <>
      <CheckboxFormField name="trackManualHours" label={t('Manually Track Hours')} form={form} />
    </>
  );

  const equipmentHourlyCostField = (form: IFormRender<IEquipmentCreateFormData>, autofocus: boolean = false) => (
    <>
      <Label>{t('Hourly Cost')}</Label>
      <CurrencyFormField
        className="hourly-cost-field"
        name="hourlyCost"
        form={form}
        validations={[{ validation: v_require }]}
        textAlign="right"
        placeholder="0.00"
        onBlur={(value) => {
          form.setData('hourlyCost', convertStringToSafeNumber(value).toFixed(2));
        }}
        autofocus={autofocus}
      />
      <Label>{t('Effective Date')}</Label>
      <div className="mb-2">
        {t(
          'The date you would like the rate change to take effect. Setting the date to the past will change past job costing data.'
        )}
      </div>
      <DatePickerFormField
        name="hourlyCostEffectiveDate"
        form={form}
        disabledBefore={
          !isNil(latestCostHistory.current?.changeDate)
            ? DateTime.fromISO(latestCostHistory.current!.changeDate).toJSDate()
            : undefined
        }
        validations={[{ validation: v_require }]}
        isOpen={dateDetails.isOpen}
        onClose={dateDetails.close}
        onOpen={dateDetails.open}
      />
    </>
  );

  const editButton = (form: IFormRender<IEquipmentCreateFormData>) => (
    <Button className="pl-5 pr-5" type="primary" onClick={form.handleSubmit}>
      {t('Save')}
    </Button>
  );

  const imageField = (form: IFormRender<IEquipmentCreateFormData>) => (
    <>
      <Label>{t('Equipment Image')}</Label>
      <ProfileImageFormField name="image" initialImage={form.state.data.imageUrl ?? null} form={form} />
    </>
  );

  const customFormField = (
    form: IFormRender<IEquipmentCreateFormData>,
    field: ICustomFieldTableRow,
    index: number,
    isAdmin: boolean
  ) => {
    const { title, subtitle, adminOnly } = field;
    const fieldName = `customField${index + 1}`;

    if (!title?.trim() || (adminOnly && !isAdmin)) {
      return null;
    }
    return (
      <div key={fieldName}>
        <Label>{title}</Label>
        <TextFormField name={fieldName} form={form} />
        {subtitle && <div className="text-sm text-gray-500">{subtitle}</div>}
      </div>
    );
  };

  const trackingDeviceFields = (form: IFormRender<IEquipmentCreateFormData>) => {
    if (!hasGpsHardware) {
      return null;
    }
    const limitingMaxDate = getLimitingMaxDate(
      devices.find((device) => device.id === Number(formData?.gpsDeviceId)) ?? null,
      selectedDevice ?? null
    );

    return (
      <>
        <Label>{t('Device')}</Label>
        <EquipmentGpsTrackerFormField
          form={form}
          name="gpsDeviceId"
          onChange={(deviceId) => setDevice(deviceId, form)}
        />
        {!isNil(form.state.data.gpsDeviceId) && (
          <>
            <Label>{t('Installation Time')}</Label>
            <Row>
              <DatePickerFormField
                name="installationDate"
                form={form}
                isOpen={installationDateOpenable.isOpen}
                onClose={installationDateOpenable.close}
                onOpen={installationDateOpenable.open}
                className="installation-date-field"
                disabledBefore={limitingMaxDate.toJSDate()}
                disabledAfter={today.endOf('day').toJSDate()}
                validations={[{ validation: v_require }]}
              />
              {form.state.data.installationTime && (
                <FeatureTimeFormField
                  name="installationTime"
                  form={form}
                  clockAction={ClockAction.CLOCK_IN}
                  className="ml-2"
                  onChange={(value) => handleTimeChange(value, form)}
                  validations={[
                    { validation: v_require },
                    {
                      validation: v_time_before_time,
                      args: {
                        time: today,
                        inclusive: true,
                        message: 'Invalid time',
                      },
                    },
                  ]}
                />
              )}
            </Row>
          </>
        )}
      </>
    );
  };

  function renderFormFields({ ...form }: IFormRender<IEquipmentCreateFormData>): ReactNode {
    switch (editKey) {
      case 'name':
        return (
          <div>
            {equipmentNameField(form)}
            {editButton(form)}
          </div>
        );
      case 'type':
        return (
          <div>
            {equipmentTypePicker(form, true)}
            {equipmentCategoryPicker(form)}
            {equipmentMakePicker(form)}
            {equipmentModelPicker(form)}
            {editButton(form)}
          </div>
        );
      case 'category':
        return (
          <div>
            {equipmentCategoryPicker(form)}
            {equipmentMakePicker(form)}
            {equipmentModelPicker(form)}
            {editButton(form)}
          </div>
        );
      case 'make':
        return (
          <div>
            {equipmentTypePicker(form, true)}
            {equipmentCategoryPicker(form)}
            {equipmentMakePicker(form)}
            {equipmentModelPicker(form)}
            {editButton(form)}
          </div>
        );
      case 'model':
        return (
          <div>
            {equipmentModelPicker(form)}
            {editButton(form)}
          </div>
        );
      case 'year':
        return (
          <div>
            {equipmentYearFormField(form, true)}
            {editButton(form)}
          </div>
        );
      case 'hourMeter':
        return (
          <div>
            {trackHoursCheckbox(form)}
            {form.state.data.trackManualHours ? equipmentHoursField(form, true) : null}
            {editButton(form)}
          </div>
        );
      case 'hourlyCost':
        return (
          <div>
            {equipmentHourlyCostField(form, true)}
            {editButton(form)}
          </div>
        );
      case 'image':
        return (
          <div>
            {imageField(form)}
            {editButton(form)}
          </div>
        );
      case 'customField1':
      case 'customField2':
      case 'customField3':
      case 'customField4':
      case 'customField5':
        if (editKey && editKey.startsWith('customField')) {
          const index = parseInt(editKey.replace('customField', ''), 10) - 1;
          if (customFields[index]) {
            return (
              <div>
                {customFormField(form, customFields[index], index, authMemberIsAdmin)}
                {editButton(form)}
              </div>
            );
          }
        }
        break;
      case 'gpsDevice':
        return hasGpsHardware ? (
          <div>
            {trackingDeviceFields(form)}
            {editButton(form)}
          </div>
        ) : null;
      case 'preOperationChecklist':
        return (
          <div>
            {preOperationChecklistField(form)}
            {editButton(form)}
          </div>
        );
      case 'postOperationChecklist':
        return (
          <div>
            {postOperationChecklistField(form)}
            {editButton(form)}
          </div>
        );
      case 'routines':
        return (
          <>
            {routineInspectionFields(form)}
            {editButton(form)}
          </>
        );
      case null:
        return (
          <div>
            {equipmentNameField(form)}
            {equipmentTypePicker(form)}
            {equipmentCategoryPicker(form)}
            {equipmentMakePicker(form)}
            {equipmentModelPicker(form)}
            {equipmentYearFormField(form)}
            {trackHoursCheckbox(form)}
            {form.state.data.trackManualHours ? equipmentHoursField(form) : null}
            {editButton(form)}
          </div>
        );
    }
  }

  const preOperationChecklistField = (form: IFormRender<IEquipmentCreateFormData>) => (
    <>
      <Label>{t('Pre-Operation Checklist')}</Label>
      <EquipmentInspectionChecklistPickerFormField name="preOperationChecklistId" form={form} />
    </>
  );

  const postOperationChecklistField = (form: IFormRender<IEquipmentCreateFormData>) => (
    <>
      <Label>{t('Post-Operation Checklist')}</Label>
      <EquipmentInspectionChecklistPickerFormField name="postOperationChecklistId" form={form} />
    </>
  );

  const routineInspectionFields = (form: IFormRender<IEquipmentCreateFormData>) => (
    <RoutineInspectionFields form={form} />
  );

  const getUnknownModel = async (categoryId: string) => {
    const results = await apolloClient.query<{ equipmentModels: EquipmentModel[] }>({
      query: equipmentUnknownModelQuery,
      variables: {
        categoryId,
      },
    });

    return _.first(results.data.equipmentModels);
  };

  const resumePendingSubmission = () => {
    const data = pendingSubmissionRef.current;
    if (data) {
      pendingSubmissionRef.current = null;
      onSubmitForm(data, true).catch(() => {
        errorToastMessage.current = t('There was an unexpected error.');
        errorToastState.open();
        loaderDetails.close();
      });
    }
  };

  async function onSubmitForm(data: IEquipmentCreateFormData, bypassDeviceCheck: boolean = false) {
    if (exceedsRoutineInspectionLimit(data)) {
      errorToastMessage.current = t(
        'You can have at most {{max}} routine inspections. Please remove the extras before saving.',
        { max: EQUIPMENT_MAX_ROUTINE_INSPECTIONS }
      );
      errorToastState.open();
      return;
    }

    const isEditingTrackingDevice = editKey === 'gpsDevice';
    const currentCoupledDevice = devices.find((device) => device.equipment_id === equipmentId) ?? null;

    const hasCoupledDevice =
      isEditingTrackingDevice &&
      hasGpsHardware &&
      !isNil(data.gpsDeviceId) &&
      !isNil(selectedDevice?.equipment_id) &&
      selectedDevice?.equipment_id !== equipmentId;

    const isRemovingTrackingDevice =
      isEditingTrackingDevice && hasGpsHardware && isNil(data.gpsDeviceId) && !isNil(currentCoupledDevice);

    if (hasCoupledDevice && !bypassDeviceCheck) {
      deviceAlreadyAssignedOpenable.open();
      pendingSubmissionRef.current = data;
      return;
    }

    if (isRemovingTrackingDevice && !bypassDeviceCheck) {
      unpairDeviceOpenable.open();
      pendingSubmissionRef.current = data;
      return;
    }
    loaderDetails.open();

    if (validateData(data)) {
      let modelId = data.modelId;

      if (!modelId) {
        try {
          const unknownModel = await getUnknownModel(data.categoryId!);
          modelId = unknownModel?.id;
        } catch {
          // fall through to the missing-model error below
        }
      }

      if (!modelId) {
        // could not find unknown model
        errorToastMessage.current = t('Cannot save without a make and model selected.');
        errorToastState.open();
        loaderDetails.close();
        return;
      }

      if (!isNil(modelToCreate)) {
        const input: EquipmentModelCreateInput = {
          createdOn: isoTimeStampUtc(),
          equipmentCategoryId: data.categoryId!,
          equipmentMakeId: data.makeId!,
          title: modelToCreate.name!,
          type: data.type!,
          id: modelToCreate.id,
        };
        createEquipmentModel(input);
      }

      editEquipment(
        equipmentId,
        data.equipmentName!,
        modelId,
        data.trackManualHours === true,
        data.year,
        data.customField1,
        data.customField2,
        data.customField3,
        data.customField4,
        data.customField5,
        data.preOperationChecklistId,
        data.postOperationChecklistId
      )
        .then(async (response) => {
          if (data.image !== formData?.image) {
            await imageUpdate(equipmentId, data.image?.file ?? null).catch(() => {
              errorToastMessage.current = t('There was an error saving the image.');
              errorToastState.open();
              loaderDetails.close();
            });
          }

          const equipmentHours = response.data.updateEquipment.id;
          let shouldSubmit = true;

          if (
            isEditingTrackingDevice &&
            hasGpsHardware &&
            selectedDevice &&
            data.gpsDeviceId &&
            data.installationDate &&
            data.installationTime
          ) {
            const installationDateTime = DateTime.fromJSDate(data.installationDate.value!).set({
              hour: data.installationTime.hour,
              minute: data.installationTime.minute,
              second: 0,
              millisecond: 0,
            });
            const coupledDate = installationDateTime.toUTC().toISO();
            const uncoupledDate = installationDateTime.minus({ seconds: 1 }).toUTC().toISO();

            let uncoupleFailed = false;
            if (!isNil(selectedDevice.equipment_id)) {
              try {
                const uncoupleResponse = await deviceRpcService.uncouple({
                  deviceId: selectedDevice.id,
                  equipmentId: selectedDevice.equipment_id,
                  uncoupledDate,
                });
                if (!isNil(uncoupleResponse.data?.error)) {
                  uncoupleFailed = true;
                  errorToastMessage.current = getDeviceCoupleErrorMessage(uncoupleResponse.data.error);
                  errorToastState.open();
                }
              } catch {
                uncoupleFailed = true;
                errorToastMessage.current = t('There was an error coupling the device.');
                errorToastState.open();
              }
            }

            if (!uncoupleFailed) {
              try {
                const coupleResponse = await deviceRpcService.couple({
                  deviceId: selectedDevice.id,
                  equipmentId,
                  coupledDate,
                });
                if (!isNil(coupleResponse.data?.error)) {
                  errorToastMessage.current = getDeviceCoupleErrorMessage(coupleResponse.data.error);
                  errorToastState.open();
                }
              } catch {
                errorToastMessage.current = t('There was an error coupling the device.');
                errorToastState.open();
              }
            }
          }

          if (isRemovingTrackingDevice && !isNil(currentCoupledDevice)) {
            try {
              const uncoupleResponse = await deviceRpcService.uncouple({
                deviceId: currentCoupledDevice.id,
                equipmentId,
                uncoupledDate: DateTime.utc().startOf('minute').toISO(),
              });
              if (!isNil(uncoupleResponse.data?.error)) {
                errorToastMessage.current = t('There was an error unpairing the device.');
                errorToastState.open();
              }
            } catch {
              errorToastMessage.current = t('There was an error unpairing the device.');
              errorToastState.open();
            }
          }

          if (data.hourMeter && data.hourMeter && data.hourMeter !== formData?.hourMeter) {
            shouldSubmit = false;
            // save the hour meter if the user input a valid value
            createEquipmentHours(equipmentHours, data.hourMeter)
              .then(() => {
                onSubmit(response.data.updateEquipment.id);
              })
              .catch(() => {
                errorToastMessage.current = t('There was an error saving the hour meter.');
                errorToastState.open();
              });
          }

          if (data.hourlyCost && data.hourlyCostEffectiveDate) {
            if (
              (isNil(latestCostHistory.current?.changeDate) && data.hourlyCost !== formData?.hourlyCost) ||
              (data.hourlyCostEffectiveDate?.value &&
                !isNil(latestCostHistory.current?.changeDate) &&
                DateTime.fromJSDate(data.hourlyCostEffectiveDate.value).startOf('day').toISO() !==
                  DateTime.fromISO(latestCostHistory.current!.changeDate).startOf('day').toISO())
            ) {
              shouldSubmit = false;
              // save the hourly cost if the user input a valid value
              createEquipmentCostHistory(
                equipmentHours,
                undefined,
                data.hourlyCost,
                DateTime.fromJSDate(data.hourlyCostEffectiveDate.value!).startOf('day').toISO()
              )
                .then(() => {
                  onSubmit(response.data.updateEquipment.id);
                })
                .catch(() => {
                  errorToastMessage.current = t('There was an error saving cost history.');
                  errorToastState.open();
                });
            } else if (!isNil(latestCostHistory.current) && data.hourlyCost !== formData?.hourlyCost) {
              shouldSubmit = false;
              // save the hourly cost if the user input a valid value
              updateEquipmentCostHistory(latestCostHistory.current.id, undefined, data.hourlyCost)
                .then(() => {
                  onSubmit(response.data.updateEquipment.id);
                })
                .catch(() => {
                  errorToastMessage.current = t('There was an error saving cost history.');
                  errorToastState.open();
                });
            }
          }

          const { routineIdsToDelete, routinesToCreate } = reconcileRoutineInspections(
            getRoutineItemsFromFormData(data),
            response.data.updateEquipment.routines
          );

          for (const routineId of routineIdsToDelete) {
            await deleteUpcomingEquipmentInspections(routineId);
          }

          for (const item of routinesToCreate) {
            try {
              await createEquipmentChecklistRoutine({
                equipmentId,
                equipmentInspectionChecklistId: item.checklistId,
                createdOn: isoTimeStampUtc(),
                recurringDays: parseRoutineInterval(item.interval),
              });
            } catch {
              errorToastMessage.current = t(
                'There was an error saving the inspection routines. Please check that all intervals are valid numbers.'
              );
              errorToastState.open();
              loaderDetails.close();
            }
          }

          if (shouldSubmit) {
            onSubmit(response.data.updateEquipment.id);
          }
        })
        .catch(() => {
          errorToastMessage.current = t('There was an error saving the equipment.');
          errorToastState.open();
          loaderDetails.close();
        });
    } else {
      errorToastMessage.current = t('Answer all of the questions and try again.');
      errorToastState.open();
      loaderDetails.close();
    }
  }

  function validateData(data: IEquipmentCreateFormData): boolean {
    if (_.isEmpty(data.equipmentName)) {
      return false;
    } else if (!_.isNil(data.makeId) && _.isNil(data.modelId)) {
      // if make is selected then we also need a model
      return false;
    }

    return true;
  }

  return (
    <div className={classes}>
      <div className="mb-6 p-8">
        {formData && !isLoadingDevices ? (
          <Form
            data={formData}
            onSubmit={onSubmitForm}
            render={renderFormFields}
            className={classes}
            allowMultipleSubmissions={true}
          />
        ) : (
          <Row justify={Justify.CENTER} className="py-8">
            <Spinner size="md" />
          </Row>
        )}

        <Toast isOpen={errorToastState.isOpen} onClose={errorToastState.close} theme={Theme.DANGER}>
          {errorToastMessage.current}
        </Toast>
        <Loader isOpen={loaderDetails.isOpen} />
        <ManageEquipmentCategoriesDialog
          isOpen={manageCategoriesOpenable.isOpen}
          onClose={handleCategoriesDialogClose}
        />

        <ManageEquipmentMakesDialog isOpen={manageMakesOpenable.isOpen} onClose={manageMakesOpenable.close} />
        <EquipmentTypesDialog isOpen={typesDialogOpenable.isOpen} onClose={typesDialogOpenable.close} />
        <EquipmentGpsDeviceAlreadyAssignedDialog
          isOpen={deviceAlreadyAssignedOpenable.isOpen}
          onClose={deviceAlreadyAssignedOpenable.close}
          onSubmit={() => {
            deviceAlreadyAssignedOpenable.close();
            resumePendingSubmission();
          }}
        />
        <UnpairTrackingDeviceDialog
          isOpen={unpairDeviceOpenable.isOpen}
          onClose={unpairDeviceOpenable.close}
          onSubmit={() => {
            unpairDeviceOpenable.close();
            resumePendingSubmission();
          }}
        />
      </div>
    </div>
  );
};

export default EditEquipmentForm;
